import { useQuery } from '@tanstack/react-query';
import { api, type AgentPresence } from '../api/client';
import { displayName } from '../utils/display';

const REFETCH_MS = 10_000;

/**
 * Team status sidebar — visible to all authenticated users (FR-12 + revised
 * FR-26). Lists every user with a colored status dot:
 *   green  Available
 *   red    In-call
 *   amber  Away
 *   grey   Offline
 */
export function TeamStatusSidebar() {
  const query = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.getAgents(),
    refetchInterval: REFETCH_MS,
    refetchIntervalInBackground: false,
  });

  if (!query.data) {
    return (
      <Card>
        <p className="text-xs text-slate-500">{query.isLoading ? 'Loading team…' : 'Team status unavailable.'}</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Team status</h3>
        <span className="text-xs text-slate-400">refreshes every 10s</span>
      </div>
      <ul className="mt-3 space-y-2">
        {query.data.agents.map(a => <AgentRow key={a.userId} agent={a} />)}
      </ul>
    </Card>
  );
}

function AgentRow({ agent }: { agent: AgentPresence }) {
  const dot =
    agent.status === 'Available'
      ? 'bg-emerald-500'
      : agent.status === 'In-call'
        ? 'bg-red-500'
        : agent.status === 'Away'
          ? 'bg-amber-500'
          : 'bg-slate-300';
  const label =
    agent.status === 'In-call' ? 'In-call' : agent.status === 'Available' ? '' : agent.status;
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden />
      <span className="flex-1 truncate text-slate-800" title={agent.email}>{displayName(agent)}</span>
      {label && <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>}
      {agent.role === 'admin' && (
        <span className="text-[10px] uppercase tracking-wider text-slate-400">admin</span>
      )}
    </li>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-4">{children}</div>;
}
